import { useAuth } from 'modules/Auth/hooks/useAuth'
import React from 'react'

import { useListenUserProfileQuery } from 'modules/Users/userApi'
import {
  useListenEnableExSortingByLevelQuery,
  useListenExLibVisibilityQuery,
  useListenShowExLibLevelsQuery,
  useSetEnableExSortingByLevelMutation,
  useSetShowExLibLevelsMutation,
} from 'modules/ExerciseLibrary/exerciseLibraryApi'
import { useListenLinkHubEnabledQuery, useSetLinkHubEnabledMutation } from 'modules/LinkHub/linkHubApi'
import {
  useListenContentFiltersEnabledQuery,
  useListenShowProgressionFilterQuery,
  useSetContentFilterEnabledMutation,
  useSetShowProgressionFilterMutation,
  useListenEnableDefaultProgressionTagQuery,
  useSetEnableDefaultProgressionTagMutation,
} from 'modules/ContentTags/contentTagsApi'
import {
  useListenMetronomeEnabledQuery,
  useListenWktDurationInflationEnabledQuery,
  useListenWktRemindersForFreeUsersQuery,
  useSetMetronomeEnabledMutation,
  useSetWktDurationInflationEnabledMutation,
  useSetWktRemindersForFreeUsersMutation,
} from './accountApi'
import { useListenCommunityEnabledQuery, useSetCommunityEnabledMutation } from 'modules/Community/communityApi'
import {
  useListenEnableQuizContentUnlockQuery,
  useSetEnableQuizContentUnlockMutation,
} from 'modules/AppOnboardingQuiz/appOnboardingQuizApi'

import { Spinner } from 'common/components/Spinner/Spinner'
import { Switch } from 'common/components/Switch/Switch'
import { exLibDisplayValues } from 'common/constants/exLibConstants'
import AccountLayout from './AccountLayout'
import ExLibDisplaySelect from './components/ExLibDisplaySelect/ExLibDisplaySelect'

export default function Advanced() {
  const { userId } = useAuth()

  const [setShowExLibLevels] = useSetShowExLibLevelsMutation()
  const [setEnableExSortingByLevel] = useSetEnableExSortingByLevelMutation()
  const [setLinkHubEnabled] = useSetLinkHubEnabledMutation()
  const [setContentFilterEnabled] = useSetContentFilterEnabledMutation()
  const [setShowProgressionFilter] = useSetShowProgressionFilterMutation()
  const [setEnableDefaultProgressionTag] = useSetEnableDefaultProgressionTagMutation()
  const [setMetronomeEnabled] = useSetMetronomeEnabledMutation()
  const [setWktDurationInflationEnabled] = useSetWktDurationInflationEnabledMutation()
  const [setCommunityEnabled] = useSetCommunityEnabledMutation()
  const [setWktRemindersForFreeUsers] = useSetWktRemindersForFreeUsersMutation()
  const [setEnableQuizContentUnlock] = useSetEnableQuizContentUnlockMutation()

  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''
  const { data: exLibVisibility } = useListenExLibVisibilityQuery({ coachOrgId })
  const { data: showExLibLevels } = useListenShowExLibLevelsQuery({ coachOrgId })
  const { data: enableExSortingByLevel } = useListenEnableExSortingByLevelQuery({ coachOrgId })
  const { data: linkHubEnabled } = useListenLinkHubEnabledQuery({ coachOrgId })
  const { data: contentFiltersEnabled } = useListenContentFiltersEnabledQuery({ coachOrgId })
  const { data: showProgressionFilter } = useListenShowProgressionFilterQuery({ coachOrgId })
  const { data: enableDefaultProgressionTag } = useListenEnableDefaultProgressionTagQuery({ coachOrgId })
  const { data: metronomeEnabled } = useListenMetronomeEnabledQuery({ coachOrgId })
  const { data: wktDurationInflationEnabled } = useListenWktDurationInflationEnabledQuery({ coachOrgId })
  const { data: communityEnabled } = useListenCommunityEnabledQuery({ coachOrgId }, { skip: !coachOrgId })
  const { data: wktRemindersForFreeUsers } = useListenWktRemindersForFreeUsersQuery({ coachOrgId })
  const { data: enableQuizContentUnlock } = useListenEnableQuizContentUnlockQuery({ coachOrgId })
  const dataLoading =
    profile === undefined ||
    profile?.isLoading ||
    exLibVisibility === undefined ||
    exLibVisibility?.isLoading ||
    showExLibLevels === undefined ||
    showExLibLevels?.isLoading ||
    linkHubEnabled === undefined ||
    linkHubEnabled?.isLoading ||
    contentFiltersEnabled === undefined ||
    contentFiltersEnabled?.isLoading ||
    showProgressionFilter === undefined ||
    showProgressionFilter?.isLoading ||
    enableDefaultProgressionTag === undefined ||
    enableDefaultProgressionTag?.isLoading ||
    metronomeEnabled === undefined ||
    metronomeEnabled?.isLoading ||
    wktDurationInflationEnabled === undefined ||
    wktDurationInflationEnabled?.isLoading ||
    enableQuizContentUnlock === undefined ||
    enableQuizContentUnlock?.isLoading ||
    wktRemindersForFreeUsers === undefined ||
    wktRemindersForFreeUsers?.isLoading

  if (dataLoading) {
    return (
      <AccountLayout>
        <Spinner className='w-10 h-10 text-gray-100 m-auto' />
      </AccountLayout>
    )
  }

  return (
    <AccountLayout>
      <div className='mb-12'>
        <h1 className='font-bold text-xl mb-4'>New User Quiz</h1>
        <Switch
          name='enableQuizContentUnlock'
          labelClasses='justify-between w-full'
          labelContainerClasses='text-sm'
          onChange={() => setEnableQuizContentUnlock({ coachOrgId, enabled: !enableQuizContentUnlock })}
          label='Unlock 1 paid workout after user completes quiz'
          isChecked={enableQuizContentUnlock === true}
        />
      </div>
      <div className='mb-12'>
        <h1 className='font-bold text-xl mb-4'>Workout Features</h1>
        <Switch
          labelClasses='justify-between w-full'
          labelContainerClasses='text-sm'
          onChange={() => setMetronomeEnabled({ coachOrgId, enabled: !metronomeEnabled })}
          label='Enable Metronome for Users'
          isChecked={metronomeEnabled === true}
        />
        <div className='mt-4'>
          <Switch
            labelClasses='justify-between w-full'
            labelContainerClasses='text-sm'
            onChange={() =>
              setWktDurationInflationEnabled({
                coachOrgId,
                enabled: wktDurationInflationEnabled === null ? false : !wktDurationInflationEnabled,
              })
            }
            label='Include Setup & Transitions'
            isChecked={wktDurationInflationEnabled === true || wktDurationInflationEnabled === null}
          />
          <p className='text-sm text-gray-500 mt-2'>
            Automatically include time for setup, transitions and pauses. Turn off to only include base exercise work
            and rest time.
          </p>
        </div>
      </div>
      <div className='mb-12'>
        <h1 className='font-bold text-xl mb-1'>Community</h1>
        <p className='text-sm text-gray-500 mb-6'>
          Gives your paid subscribers a community feed in the app where they can post, reply, and like. Turning this on
          also adds a Community page to this dashboard where you can post to every paid subscriber.
        </p>
        <Switch
          name='communityEnabled'
          labelClasses='justify-between w-full'
          labelContainerClasses='text-sm'
          onChange={() => setCommunityEnabled({ coachOrgId, enabled: communityEnabled !== true })}
          label='Enable Community for Users'
          isChecked={communityEnabled === true}
        />
      </div>
      <div className='mb-12'>
        <h1 className='font-bold text-xl mb-1'>Workout Reminders</h1>
        <p className='text-sm text-gray-500 mb-6'>
          Reminds members to work out on the days they pick. By default only paying members get them. Turn this on to
          include free members.
        </p>
        <Switch
          name='wktRemindersForFreeUsers'
          labelClasses='justify-between w-full'
          labelContainerClasses='text-sm'
          onChange={() => setWktRemindersForFreeUsers({ coachOrgId, enabled: wktRemindersForFreeUsers !== true })}
          label='Enable Workout Reminders for Free Users'
          isChecked={wktRemindersForFreeUsers === true}
        />
      </div>
      <div className='mb-12'>
        <h1 className='font-bold text-xl mb-4'>Additional Resources</h1>
        <Switch
          labelClasses='justify-between w-full'
          labelContainerClasses='text-sm'
          onChange={() => setLinkHubEnabled({ coachOrgId, enabled: !linkHubEnabled })}
          label='Display Additional Resources to Users'
          isChecked={linkHubEnabled === true}
        />
      </div>
      <div className='mb-12'>
        <h1 className='font-bold text-xl mb-1'>Exercise Library</h1>
        <p className='text-sm text-gray-500 mb-6'>Shows a dedicated Exercise Library to users in the app. </p>
        <div
          className={`flex flex-col lg:flex-row justify-between items-start ${Boolean(exLibVisibility) && exLibVisibility !== exLibDisplayValues.hidden ? 'lg:items-start' : 'lg:items-center'}`}
        >
          <div className='mr-5'>
            <h3 className='mb-2 text-gray-700 font-semibold flex text-sm'>Exercise Library Visibilty to Users</h3>
            {Boolean(exLibVisibility) && exLibVisibility !== exLibDisplayValues.hidden && (
              <p className='text-sm text-gray-500'>
                Exercises will only be shown if they are properly tagged with a “Tag” and “Level”.
              </p>
            )}
          </div>
          <ExLibDisplaySelect coachOrgId={coachOrgId} />
        </div>
        {Boolean(exLibVisibility) && exLibVisibility !== exLibDisplayValues.hidden && (
          <>
            <div className='mt-8'>
              <Switch
                labelClasses='justify-between w-full'
                labelContainerClasses='text-sm'
                onChange={() => setShowExLibLevels({ coachOrgId, show: !showExLibLevels })}
                label='Display “Level” to Users'
                isChecked={showExLibLevels === true}
              />
              <p className='text-sm text-gray-500 mt-2'>The Level value can be optionally displayed to users.</p>
            </div>
            {!Boolean(showExLibLevels) && (
              <div className='mt-8'>
                <Switch
                  labelClasses='justify-between w-full'
                  labelContainerClasses='text-sm'
                  onChange={() => setEnableExSortingByLevel({ coachOrgId, enable: !enableExSortingByLevel })}
                  label='Enable Sorting by “Level”'
                  isChecked={enableExSortingByLevel}
                />
                <p className='text-sm text-gray-500 mt-2'>
                  Allows sorting by Level even if Level is not displayed to users.
                </p>
              </div>
            )}
          </>
        )}
      </div>
      <div className='mb-12'>
        <h1 className='font-bold text-xl mb-4'>Content Filters</h1>
        <div className='space-y-4'>
          <Switch
            labelClasses='justify-between w-full'
            labelContainerClasses='text-sm'
            onChange={() =>
              setContentFilterEnabled({
                coachOrgId,
                enabled: contentFiltersEnabled?.duration === false,
                name: 'duration',
              })
            }
            label='Display Duration Filters to Users'
            isChecked={contentFiltersEnabled?.duration !== false}
          />
          <Switch
            labelClasses='justify-between w-full'
            labelContainerClasses='text-sm'
            onChange={() =>
              setContentFilterEnabled({
                coachOrgId,
                enabled: contentFiltersEnabled?.difficulty === false,
                name: 'difficulty',
              })
            }
            label='Display Difficulty Filters to Users'
            isChecked={contentFiltersEnabled?.difficulty !== false}
          />
          <Switch
            labelClasses='justify-between w-full'
            labelContainerClasses='text-sm'
            onChange={() =>
              setContentFilterEnabled({
                coachOrgId,
                enabled: contentFiltersEnabled?.equipment === false,
                name: 'equipment',
              })
            }
            label='Display Equipment Filters to Users'
            isChecked={contentFiltersEnabled?.equipment !== false}
          />
          <Switch
            labelClasses='justify-between w-full'
            labelContainerClasses='text-sm'
            onChange={() =>
              setContentFilterEnabled({
                coachOrgId,
                enabled: contentFiltersEnabled?.instructors === false,
                name: 'instructors',
              })
            }
            label='Display Instructor Filters to Users'
            isChecked={contentFiltersEnabled?.instructors !== false}
          />
          <Switch
            labelClasses='justify-between w-full'
            labelContainerClasses='text-sm'
            onChange={() =>
              setContentFilterEnabled({ coachOrgId, enabled: contentFiltersEnabled?.tags === false, name: 'tags' })
            }
            label='Display Tag Filters to Users'
            isChecked={contentFiltersEnabled?.tags !== false}
          />
        </div>
      </div>
      <div>
        <h1 className='font-bold text-xl mb-4'>Exercise Progressions</h1>
        <Switch
          labelClasses='justify-between w-full'
          labelContainerClasses='text-sm'
          onChange={() => setShowProgressionFilter({ coachOrgId, show: !showProgressionFilter })}
          label='Display Exercise Progression Filters to Users'
          isChecked={showProgressionFilter === true}
        />
        {showProgressionFilter === true && (
          <div className='mt-4'>
            <Switch
              labelClasses='justify-between w-full'
              labelContainerClasses='text-sm'
              onChange={() => setEnableDefaultProgressionTag({ coachOrgId, enabled: !enableDefaultProgressionTag })}
              label='Enable Default Progression Filter Tags'
              isChecked={enableDefaultProgressionTag === true}
            />
            <p className='text-sm text-gray-500 mt-2'>
              Allows setting a default progression filter tag per exercise in the program. When a user opens
              progressions for that exercise in the mobile app, the filter will start on the selected tag.
            </p>
          </div>
        )}
      </div>
    </AccountLayout>
  )
}
